import { NavLink } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { useJukebox } from '../../application/sound/JukeboxProvider';
import { PRIMARY_NAVIGATION } from '../../app/routes';
import { FlagIcon, GarageIcon } from '../icons/ControlIcons';
import { EqualiserIcon } from '../icons/JukeboxIcons';

/**
 * The primary areas, always visible, always in the same order.
 *
 * THREE TABS, and the shape of this file is why the count is not a fact anything
 * here knows: `PRIMARY_NAVIGATION` is the table, `ICONS` is the lookup, and a
 * tab is a row in `routes.js` plus a glyph here (SPEC-007, SPEC-011). Nothing in
 * this component counts, measures or special-cases the number of tabs; the sheet
 * lays them out with `flex: 1`, so three tabs are thirds and five are fifths —
 * which is what made removing the duel's tab a deleted row rather than a
 * relayout (SPEC-008 PR-8.13).
 *
 * The §12 composition's other tabs — Collection, Events, Rankings, Profile —
 * still have nothing behind them and are still out of the bar. So, for now, is
 * the Overtake: a tab that opens something half-built is worse than a tab that
 * is not there, and `routes.js` is where that decision is written down.
 *
 * ONE TAB REPORTS STATE, and it is the only reason this component knows the
 * jukebox exists. The soundtrack keeps playing after the reader has walked away
 * from its screen, so the bar — which is on screen wherever they walked to — is
 * the right place to say so. It says it twice: the meter moves, and a
 * visually-hidden word goes in beside the label, because an animation is nothing
 * to a screen reader and stands still under reduced motion (NFR-04).
 *
 * Every href is locale-prefixed, so tapping a tab can never drop the visitor out
 * of their language (CON-002).
 */
const ICONS = {
    garage: GarageIcon,
    duel: FlagIcon,
    jukebox: EqualiserIcon,
};

export default function BottomNavigation() {
    const { localeCode } = useLocale();
    const t = useTranslate();
    // `null` outside a provider, which every existing screen test relies on:
    // nothing in the bar depends on the jukebox existing.
    const jukebox = useJukebox();

    return (
        <nav className="tab-bar" aria-label={t('a11y.tabBar')}>
            {PRIMARY_NAVIGATION.map(({ key, path, messageKey }) => {
                const NavigationIcon = ICONS[key];
                const isPlaying = key === 'jukebox' && Boolean(jukebox?.isPlaying);

                return (
                    <NavLink
                        key={key}
                        to={`/${localeCode}${path}`}
                        end={path === ''}
                        className={`tab-bar__item${isPlaying ? ' tab-bar__item--playing' : ''}`}
                    >
                        <NavigationIcon className="tab-bar__icon" size={22} />
                        <span className="tab-bar__label">{t(messageKey)}</span>
                        {isPlaying ? <span className="visually-hidden">{t('jukebox.playing')}</span> : null}
                    </NavLink>
                );
            })}
        </nav>
    );
}
